<template>
  <section class="mb-hero">
    <div class="mb-hero__bg" aria-hidden="true">
      <span class="mb-hero__orb mb-hero__orb--1" />
      <span class="mb-hero__orb mb-hero__orb--2" />
    </div>
    <div class="mb-wrap mb-hero__inner">
      <div class="mb-hero__copy">
        <p v-if="label" class="mb-hero__label">{{ label }}</p>
        <h1 class="mb-hero__title">{{ title }}</h1>
        <p class="mb-hero__desc">{{ description }}</p>
        <router-link class="mb-btn mb-hero__btn" :to="ctaTo">
          {{ ctaLabel }}
          <span aria-hidden="true">→</span>
        </router-link>
        <ul v-if="points && points.length" class="mb-hero__points">
          <li v-for="p in points" :key="p.title">
            <span class="mb-hero__point-icon" v-html="glyphs[p.icon] || glyphs.shield" />
            <strong class="mb-hero__point-title">{{ p.title }}</strong>
            <span v-if="p.desc" class="mb-hero__point-desc">{{ p.desc }}</span>
          </li>
        </ul>
      </div>
      <div class="mb-hero__visual" aria-hidden="true">
        <div class="mb-hero__art-glow" />
        <img
          class="mb-hero__art"
          :src="heroIllustration"
          alt=""
          width="800"
          height="539"
          loading="eager"
          decoding="async"
        />
        <span class="mb-hero__badge">100%<br>FREE</span>
      </div>
    </div>
    <div class="mb-hero__wave" aria-hidden="true">
      <svg viewBox="0 0 1440 64" preserveAspectRatio="none">
        <path d="M0 32c240 48 480-16 720 16s480-16 720 16v32H0V32z" fill="#fff" />
      </svg>
    </div>
  </section>
</template>

<script>
import heroIllustration from '@/assets/myvpnbook/hero-illustration.svg'
import { DOWNLOAD_PATH } from '@/config/myvpnbookSite'

const icon = (paths) => `<svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">${paths}</svg>`

export default {
  name: 'MyvpnbookHeroSection',
  props: {
    label: { type: String, default: 'MYVPNBOOK' },
    title: { type: String, required: true },
    description: { type: String, default: '' },
    ctaLabel: { type: String, default: 'Get Free VPN' },
    ctaTo: { type: String, default: DOWNLOAD_PATH },
    points: { type: Array, default: () => [] },
  },
  data() {
    return {
      heroIllustration,
      glyphs: {
        shield: icon(
          '<path d="M12 3l7 2.6v5.2c0 4.4-2.9 8.2-7 9.7-4.1-1.5-7-5.3-7-9.7V5.6L12 3z" stroke="currentColor" stroke-width="1.7"/><path d="M9.2 12.1l1.8 1.8 3.8-4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>'
        ),
        lock: icon(
          '<rect x="6" y="10" width="12" height="9" rx="2" stroke="currentColor" stroke-width="1.7"/><path d="M8.5 10V8a3.5 3.5 0 017 0v2" stroke="currentColor" stroke-width="1.7"/>'
        ),
        devices: icon(
          '<rect x="3" y="5" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.7"/><rect x="9" y="10" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.7"/>'
        ),
        globe: icon(
          '<circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.7"/><path d="M4 12h16M12 4c2.2 2.4 3.3 5.1 3.3 8S14.2 17.6 12 20c-2.2-2.4-3.3-5.1-3.3-8S9.8 6.4 12 4z" stroke="currentColor" stroke-width="1.7"/>'
        ),
        eye: icon(
          '<path d="M2.5 12S6 6.5 12 6.5 21.5 12 21.5 12 18 17.5 12 17.5 2.5 12 2.5 12z" stroke="currentColor" stroke-width="1.7"/><circle cx="12" cy="12" r="2.4" stroke="currentColor" stroke-width="1.7"/>'
        ),
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.mb-wrap {
  width: var(--vpn-content-width);
  max-width: 100%;
  margin: 0 auto;
}

.mb-hero {
  position: relative;
  background: #eff6ff;
  padding: 48px 0 0;
  overflow: hidden;
}

.mb-hero__bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.mb-hero__orb {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(27, 111, 232, 0.12) 0%, transparent 70%);
}

.mb-hero__orb--1 {
  width: 320px;
  height: 320px;
  top: -80px;
  right: 8%;
}

.mb-hero__orb--2 {
  width: 200px;
  height: 200px;
  bottom: 80px;
  right: 42%;
  opacity: 0.7;
}

.mb-hero__inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(300px, 0.92fr);
  gap: 32px 40px;
  align-items: center;
  padding-bottom: 40px;
}

.mb-hero__label {
  margin: 0 0 14px;
  color: #7a8da8;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2em;
}

.mb-hero__title {
  margin: 0;
  max-width: 580px;
  color: #0f172a;
  font-size: clamp(36px, 4.6vw, 54px);
  line-height: 1.1;
  letter-spacing: -0.03em;
  font-weight: 800;
}

.mb-hero__desc {
  max-width: 540px;
  margin: 18px 0 28px;
  color: #5b6478;
  font-size: 17px;
  line-height: 1.65;
}

.mb-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 999px;
  background: var(--vpn-brand);
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  box-shadow: var(--vpn-shadow-btn);
}

.mb-hero__btn {
  min-height: 52px;
  padding: 0 28px;
  font-size: 16px;
}

.mb-hero__points {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px 16px;
  margin: 36px 0 0;
  padding: 0;
  max-width: 620px;
  list-style: none;
}

.mb-hero__points li {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  color: var(--vpn-ink);
}

.mb-hero__point-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--vpn-brand);
}

.mb-hero__point-icon ::v-deep svg {
  width: 28px;
  height: 28px;
}

.mb-hero__point-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.mb-hero__point-desc {
  display: block;
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
}

.mb-hero__visual {
  position: relative;
  min-height: 340px;
  padding: 12px 0 0 8px;
}

.mb-hero__art-glow {
  position: absolute;
  top: 8%;
  left: 12%;
  width: 78%;
  height: 72%;
  border-radius: 32px;
  background: radial-gradient(circle at 40% 35%, rgba(27, 111, 232, 0.22), rgba(27, 111, 232, 0) 68%);
  filter: blur(18px);
  pointer-events: none;
}

.mb-hero__art {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  max-width: 520px;
  height: auto;
  margin-left: auto;
}

.mb-hero__badge {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(145deg, #34d399, #16a34a);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.15;
  text-align: center;
  box-shadow: 0 12px 28px rgba(22, 163, 74, 0.4);
}

.mb-hero__wave {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  line-height: 0;
  margin-top: -1px;
}

.mb-hero__wave svg {
  display: block;
  width: 100%;
  height: 56px;
}

@media (max-width: 800px) {
  .mb-hero__inner {
    grid-template-columns: 1fr;
  }

  .mb-hero__points {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: none;
  }

  .mb-hero__visual {
    min-height: 280px;
    padding: 0;
  }

  .mb-hero__art {
    margin: 0 auto;
  }

  .mb-hero__badge {
    top: -4px;
    right: 2%;
    width: 80px;
    height: 80px;
    font-size: 13px;
  }
}
</style>
